import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ActionsDropdown, ActionItem } from "@/components/ui/actions-dropdown";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export interface BreadcrumbEntry {
  path: string;
  label: string;
}

interface DetailPageLayoutProps {
  /** Back button label */
  backLabel: string;
  /** Callback when back button is clicked */
  onBack: () => void;
  /** Actions for the dropdown menu in the header */
  actions?: ActionItem[];
  /** Optional breadcrumb trail rendered between header and hero */
  breadcrumbs?: BreadcrumbEntry[];
  /** Hero section: icon/image element (rendered inside the 16x16 rounded container) */
  icon: ReactNode;
  /** Title text */
  title: string;
  /** Optional subtitle below the title */
  subtitle?: string;
  /** Badges/tags rendered inline with the title */
  badges?: ReactNode;
  /** Optional description below the title row */
  description?: string;
  /** Additional hero content below icon+title (e.g. badge row) */
  heroExtra?: ReactNode;
  /** Main scrollable content area */
  children: ReactNode;
  /** Optional content rendered after the scrollable area (dialogs, etc.) */
  dialogs?: ReactNode;
}

export function DetailPageLayout({
  backLabel,
  onBack,
  actions,
  breadcrumbs,
  icon,
  title,
  subtitle,
  badges,
  description,
  heroExtra,
  children,
  dialogs,
}: DetailPageLayoutProps) {
  return (
    <div className="h-full flex flex-col">
      {/* Sticky Header Bar */}
      <div className="border-b bg-card/30">
        <div className="flex items-center justify-between px-6 py-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backLabel}
          </Button>
          {actions && actions.length > 0 && (
            <ActionsDropdown align="end" actions={actions} />
          )}
        </div>

        {/* Breadcrumb Trail */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="px-6 pb-2">
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <BreadcrumbItem key={crumb.path}>
                    {index > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbLink asChild>
                      <Link to={crumb.path}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                ))}
                <BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbPage>{title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        )}
        {/* Hero Section */}
        <div className="px-6 pb-8 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
              {icon}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-semibold">{title}</h1>
                {badges}
              </div>
              {subtitle && (
                <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
              )}
              {description && (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              )}
              {heroExtra && <div className="mt-3">{heroExtra}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {children}
        </div>
      </div>

      {/* Dialogs rendered outside scroll area */}
      {dialogs}
    </div>
  );
}
